import { useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { keyframes } from '@emotion/css';

import { normalizarRuta } from '#uso/privacidad';

import { hacerFoto, SIN_FOTO } from './captura';
import type { FotoPantalla } from './captura';
import { HojaAvisarProblema } from './HojaAvisarProblema';

const destello = keyframes({
  '0%': { opacity: 0 },
  '25%': { opacity: 0.92 },
  '100%': { opacity: 0.92 },
});

/** Espera a que se pinte lo último (la brújula ya cerrada). */
function trasPintar(ms = 0): Promise<void> {
  return new Promise(resolve =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, ms)),
    ),
  );
}

type Paso =
  | { tipo: 'cerrado' }
  | { tipo: 'foto' }
  | { tipo: 'hoja'; foto: FotoPantalla | null; pantalla: string };

/**
 * «Avisar de un problema aquí»: `abrir` hace la foto de lo que se ve (con un
 * destello blanco que tapa el instante en que se prepara) y abre la hoja.
 * `elemento` se pinta donde se use (el destello y la hoja van en portal).
 */
export function useAvisarProblema(): {
  abrir: () => void;
  elemento: ReactNode;
} {
  const [paso, setPaso] = useState<Paso>({ tipo: 'cerrado' });

  const abrir = async () => {
    const pantalla = normalizarRuta(window.location.pathname);
    // La brújula se cierra antes: que no salga en la foto.
    await trasPintar(120);
    setPaso({ tipo: 'foto' });
    await trasPintar(60);
    let foto: FotoPantalla | null = null;
    try {
      foto = await hacerFoto();
    } catch {
      // Sin foto: la hoja deja enviar solo el texto.
    }
    setPaso({ tipo: 'hoja', foto, pantalla });
  };

  const elemento =
    paso.tipo === 'foto' ? (
      createPortal(
        <div
          {...{ [SIN_FOTO]: '' }}
          aria-hidden
          data-testid="avisar-problema-destello"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            backgroundColor: '#ffffff',
            // Que no estorbe al mirar qué cifras se ven (elementFromPoint).
            pointerEvents: 'none',
            animation: `${destello} 180ms ease-out both`,
          }}
        />,
        document.body,
      )
    ) : paso.tipo === 'hoja' ? (
      <HojaAvisarProblema
        foto={paso.foto}
        pantalla={paso.pantalla}
        onCerrar={() => setPaso({ tipo: 'cerrado' })}
      />
    ) : null;

  return { abrir: () => void abrir(), elemento };
}
